import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Mail, MapPin, Phone, Send, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, TextArea } from '@/components/ui/Input';
import { Section, Container } from '@/components/ui/Section';
import { Card } from '@/components/ui/Card';
import { LocalizedLink } from '@/components/LocalizedLink';

export function Contacts() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate form submission
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <>
      <Helmet>
        <title>{t('contacts.pageTitle')}</title>
        <meta name="description" content={t('contacts.metaDescription')} />
        <link rel="canonical" href={`https://www.01sistemi.it/${currentLang}/contatti/`} />
      </Helmet>

      {/* Hero Section */}
      <Section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-100/80 via-rose-50/90 to-pink-50/80" />
        <Container className="relative">
          <div className="text-center max-w-2xl mx-auto">
            <span className="caption text-primary-400">{t('contacts.overline')}</span>
            <h1 className="heading-1 mt-4">{t('contacts.heroTitle')}</h1>
            <p className="body-large mt-4">
              {t('contacts.heroDescription')}
            </p>
          </div>
        </Container>
      </Section>

      {/* Contact Section */}
      <Section>
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-stretch">
            {/* Contact Form */}
            <div className="h-full">
              <Card padding="lg" className="h-full flex flex-col">
                <h2 className="heading-3 mb-6">{t('contacts.formTitle')}</h2>

                {isSubmitted ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 text-accent-400 mx-auto mb-4" />
                    <h3 className="heading-4 mb-2">{t('contacts.success.title')}</h3>
                    <p className="body-regular">
                      {t('contacts.success.message')}
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6 flex flex-col flex-grow">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Input
                        label={t('contacts.form.nameLabel')}
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder={t('contacts.form.namePlaceholder')}
                      />
                      <Input
                        label={t('contacts.form.emailLabel')}
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder={t('contacts.form.emailPlaceholder')}
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <Input
                        label={t('contacts.form.companyLabel')}
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder={t('contacts.form.companyPlaceholder')}
                      />
                      <Input
                        label={t('contacts.form.phoneLabel')}
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={t('contacts.form.phonePlaceholder')}
                      />
                    </div>

                    <div className="flex-grow flex flex-col">
                      <TextArea
                        label={t('contacts.form.messageLabel')}
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={12}
                        placeholder={t('contacts.form.messagePlaceholder')}
                        className="flex-grow"
                      />
                    </div>

                    <Button type="submit" variant="primary" className="w-full">
                      <Send className="w-5 h-5 mr-2" />
                      {t('contacts.form.submitButton')}
                    </Button>

                    <p className="text-xs text-gray-500 text-center">
                      {t('contacts.form.privacyText')} {' '}
                      <LocalizedLink to="privacy" className="text-primary-400 hover:underline">
                        {t('contacts.form.privacyPolicy')}
                      </LocalizedLink>
                    </p>
                  </form>
                )}
              </Card>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="heading-3 mb-6">{t('contacts.infoTitle')}</h2>
                <p className="body-regular mb-8">
                  {t('contacts.availability')}
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('contacts.address')}</h3>
                    <p className="text-gray-600">
                      Via Malpasso, 37<br />
                      52037 Sansepolcro (AR)<br />
                      Italia
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('contacts.phone')}</h3>
                    <a href="tel:+3905751943310" className="text-gray-600 hover:text-primary-400 transition-colors">
                      +39 0575 194 33 10
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('contacts.email')}</h3>
                    <a href="mailto:info@01s.it" className="text-gray-600 hover:text-primary-400 transition-colors">
                      info@01s.it
                    </a>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="aspect-video bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <MapPin className="w-8 h-8 mx-auto mb-2" />
                  <p>{t('contacts.mapPlaceholder')}</p>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}
